import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs';

/** Material internals; see {@link ComboboxComponent.onOptionSelected} for why. */
type MultiSelectTrigger = {
  closePanel?: () => void;
  _closingActionsSubscription?: { unsubscribe(): void };
  _subscribeToClosingActions(): { unsubscribe(): void };
};

@Component({
  selector: 'app-combobox',
  standalone: true,
  imports: [CommonModule, FormsModule, MatAutocompleteModule],
  styles: `
    .combobox-wrapper {
      position: relative;
    }

    .combobox-input {
      padding-right: 36px;
    }

    .combobox-wrapper.combobox-no-border .combobox-input {
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
    }

    .combobox-chevron {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 16px;
      color: var(--gray-500);
      pointer-events: none;
    }

    .combobox-state {
      color: var(--gray-500);
      font-size: 14px;
    }

    .combobox-action {
      display: block;
      width: 100%;
      padding: 8px 16px;
      border: none;
      background: none;
      text-align: left;
      font-size: 14px;
      font-weight: 500;
      color: var(--brand-500);
      cursor: pointer;
    }

    .combobox-action:hover {
      background: var(--gray-50);
    }
  `,
  template: `
    <div class="combobox-wrapper" [class.combobox-no-border]="noBorder()">
      <input
        #searchInput
        class="form-control combobox-input"
        [ngModel]="searchValue()"
        (ngModelChange)="onSearchChange($event)"
        [matAutocomplete]="auto"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel() || null"
        [attr.aria-busy]="loading()"
        [attr.readonly]="selectOnly() ? '' : null"
        [style.color]="textColor()"
        (click)="onInputClick()"
        (focus)="inputFocus.emit()"
        (keydown.ArrowDown)="onInputArrowDown()"
      />
      <i class="bi bi-chevron-down combobox-chevron"></i>
    </div>

    <!-- mat-autocomplete forwards "class" to the overlay panel; "panelClass" is
         mat-select's and is ignored here. -->
    <mat-autocomplete
      #auto
      class="combobox-panel"
      [panelWidth]="panelWidth()"
      [displayWith]="displayWith"
      (optionSelected)="onOptionSelected($event)"
      (opened)="syncSelectedOption()"
      (closed)="onPanelClosed()"
    >
      <!-- An action lives outside the option list so the keyboard and the selection model never reach it. mousedown is
           swallowed so the input keeps focus until the click lands. -->
      @if (actionLabel()) {
        <button
          type="button"
          class="combobox-action"
          (mousedown)="$event.preventDefault()"
          (click)="onActionClick()"
        >
          {{ actionLabel() }}
        </button>
      }
      <mat-option *ngFor="let item of items()" [value]="item">
        @if (item && itemTemplate()) {
          <ng-container
            *ngTemplateOutlet="
              itemTemplate() ?? null;
              context: { $implicit: item }
            "
          />
        }
      </mat-option>
      @if (errorText()) {
        <mat-option class="combobox-state" disabled>{{
          errorText()
        }}</mat-option>
      } @else if (loading()) {
        <mat-option class="combobox-state" disabled>{{
          loadingText()
        }}</mat-option>
      } @else if (!items().length && emptyText()) {
        <mat-option class="combobox-state" disabled>{{
          emptyText()
        }}</mat-option>
      }
    </mat-autocomplete>
  `,
})
export class ComboboxComponent<T> {
  private readonly destroyRef = inject(DestroyRef);

  private isDestroyed = false;

  control = input.required<FormControl<T | null>>();

  items = input<T[]>([]);

  placeholder = input('');

  ariaLabel = input('');

  disabled = input(false);

  loading = input(false);

  loadingText = input('');

  emptyText = input('');

  errorText = input('');

  noBorder = input(false);

  /**
   * Overrides the dropdown width, which otherwise matches the input. `auto`
   * sizes it to its rows — what a narrow toolbar control needs when its options
   * are richer than one line of text.
   */
  panelWidth = input('');

  selectOnly = input(false);

  enableUnselect = input(false);

  /**
   * Opt-in multi-select: the control is left alone, picks leave through
   * {@link itemPicked}, and the panel stays open so rows can be toggled.
   */
  multiple = input(false);

  /** Which options carry a checkmark in `multiple` mode. */
  isPicked = input<(item: T) => boolean>(() => false);

  textColor = input<string | null>(null);

  itemTemplate = input<TemplateRef<{ $implicit: T }>>();

  displayValue = input<((item: T) => string) | undefined>();

  searchValue = model('');

  /** Label of the panel's action row; the row only exists when set. */
  actionLabel = input('');

  /**
   * Pairs an option with the control's value for the checkmark. Identity by
   * default, which a server-side list outlives: a host whose options are
   * refetched should compare by id.
   */
  compareWith = input<(option: T, value: T) => boolean>(Object.is);

  searchChange = output<string>();

  inputFocus = output<void>();

  /** The action row was clicked; the panel is closed, nothing was selected. */
  actionSelected = output<void>();

  /** A pick in `multiple` mode; the control is left alone. */
  itemPicked = output<T>();

  private readonly searchInput =
    viewChild.required<ElementRef<HTMLInputElement>>('searchInput');

  private readonly trigger = viewChild.required(MatAutocompleteTrigger);

  private readonly autocomplete = viewChild<MatAutocomplete>(MatAutocomplete);

  displayWith = (_item: T | null): string => this.searchValue();

  constructor() {
    // Register before takeUntilDestroyed so the guard is set before a pending
    // debounce can flush during Angular's destruction callbacks.
    this.destroyRef.onDestroy(() => (this.isDestroyed = true));

    // Re-rendered options never carry selection state — a refetched list, or a
    // control written programmatically, loses the checkmark. Re-derive it from
    // the control whenever the option list changes while the panel shows it.
    effect((onCleanup) => {
      const autocomplete = this.autocomplete();
      if (!autocomplete) return;
      const subscription = autocomplete.options.changes.subscribe(() =>
        this.syncSelectedOption()
      );
      onCleanup(() => subscription.unsubscribe());
    });

    toObservable(this.searchValue)
      .pipe(
        skip(1),
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        if (!this.isDestroyed) {
          this.searchChange.emit(value);
        }
      });
  }

  onInputClick(): void {
    if (this.selectOnly()) this.trigger().openPanel();
  }

  onActionClick(): void {
    this.trigger().closePanel();
    this.actionSelected.emit();
  }

  /** Marks the options counted as picked, silently. */
  syncSelectedOption(): void {
    const autocomplete = this.autocomplete();
    if (!autocomplete) return;
    const { value } = this.control();
    const compare = this.compareWith();
    // One control value in single-select, the host's whole list in `multiple`.
    const isPicked = this.multiple()
      ? this.isPicked()
      : (item: T) => value != null && compare(item, value);
    for (const option of autocomplete.options) {
      const selected = option.value != null && isPicked(option.value as T);
      if (selected !== option.selected) {
        selected ? option.select(false) : option.deselect(false);
      }
    }
  }

  onInputArrowDown(): void {
    if (this.selectOnly()) this.trigger().openPanel();
  }

  onSearchChange(value: string | T) {
    if (typeof value !== 'string') return;
    // Material writes the pick back through ngModel, raw, before announcing it;
    // in `multiple` mode that would replace the search term. Only the field's
    // own text is what someone typed. Only string items can collide.
    if (this.multiple() && value !== this.searchInput().nativeElement.value) {
      return;
    }
    this.searchValue.set(value);
  }

  /**
   * Hands the field back to what it shows when empty — in `multiple` mode the
   * host's summary of the picks, which the search term was covering up. A pick
   * does not come through here: its close is suppressed.
   */
  protected onPanelClosed(): void {
    if (this.multiple()) this.searchValue.set('');
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const item = event.option.value as T;
    if (this.multiple()) {
      this.itemPicked.emit(item);
      // The search term is left alone; rewriting it refetches the list being
      // picked from. Reopening after Material's close is no cure either — the
      // rebuilt panel spends a frame unlaid-out, then scrolls to the top. So the
      // close is swallowed by a stand-in that steps aside as it goes, re-arming
      // the `take(1)` closing-action subscription the reopen used to renew;
      // without that, later picks and click-away are ignored. A Material rename
      // breaks picking, which the spec covers.
      const trigger = this.trigger() as unknown as MultiSelectTrigger;
      trigger.closePanel = () => {
        delete trigger.closePanel;
        trigger._closingActionsSubscription?.unsubscribe();
        trigger._closingActionsSubscription =
          trigger._subscribeToClosingActions();
      };
      // Material has already cleared the other rows' checkmarks by now.
      this.syncSelectedOption();
      return;
    }
    if (this.enableUnselect() && item === this.control().value) {
      this.searchValue.set('');
      this.control().setValue(null);
    } else {
      this.searchValue.set(
        this.displayValue()?.(item) ?? event.option.viewValue
      );
      this.control().setValue(item);
    }
  }
}
